import { env } from '@env/index.mjs'
import { Percent } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { SessionContextValue, signOut } from 'next-auth/react'
import { GoSignOut } from 'react-icons/go'

import { Button } from '../../../_components/ui/button'
import getButtonIcon from '../_helpers/get-button-icon'
import useClinicIsActive from '../_hooks/use-clinic-is-active'
import { useIsSimulationActive } from '../_hooks/use-is-simulation-active'
import usePatientIsActive from '../_hooks/use-patient-is-active'
import AccessControl from './access-control'
import {
  Sidebar as _Sidebar,
  SidebarButton,
  SidebarButtonImage,
  SidebarButtonText,
  SidebarContent,
  SidebarHeader,
  SidebarHeaderBrandText,
  SidebarHeaderImage,
  SidebarHeaderText,
  SidebarItems,
} from './ui/sidebar'

interface SidebarProps {
  session: SessionContextValue
}

const Sidebar = ({ session }: SidebarProps) => {
  const router = useRouter()
  const clinicIsActive = useClinicIsActive()
  const patientIsActive = usePatientIsActive()
  const isSimulationActive = useIsSimulationActive()

  const adminClinicIcon = getButtonIcon(
    clinicIsActive,
    '/icons/hospital-icon-active.svg',
    '/icons/hospital-icon-default.svg'
  )

  const adminPatientIcon = getButtonIcon(
    patientIsActive,
    '/icons/patient-icon-active.svg',
    '/icons/patient-icon-default.svg'
  )

  const userPatientIcon = getButtonIcon(
    clinicIsActive,
    '/icons/patient-icon-active.svg',
    '/icons/patient-icon-default.svg'
  )

  const simulationIcon = isSimulationActive ? (
    <Percent
      className="text-brand-green-primary-5"
      size={18}
      aria-label="Simulação de crédito"
    />
  ) : (
    <Percent
      className="text-brand-grayscale-7"
      size={18}
      aria-label="Simulação de crédito"
    />
  )

  const handleSignOutClick = async () => {
    await signOut({
      redirect: false,
    })

    router.push(`${env.NEXT_PUBLIC_DOMAIN_URL}/dashboard/login`)
  }

  return (
    <_Sidebar>
      <SidebarHeader>
        <SidebarHeaderImage src="/icons/logo-text.svg" alt="Logo da Dr.Sim" />
        <SidebarHeaderText>
          Democratizamos o acesso{' '}
          <SidebarHeaderBrandText>à saúde de qualidade.</SidebarHeaderBrandText>
        </SidebarHeaderText>
      </SidebarHeader>

      <SidebarContent>
        <SidebarItems>
          <AccessControl
            role={session.data?.user.role}
            renderIfAdmin={
              <SidebarButton
                href="/dashboard/clinics"
                isActive={clinicIsActive}
              >
                <SidebarButtonImage
                  src={adminClinicIcon}
                  alt="Ícone de hospital"
                />
                <SidebarButtonText>Instituições</SidebarButtonText>
              </SidebarButton>
            }
            renderIfUser={null}
          />
          <AccessControl
            role={session.data?.user.role}
            renderIfAdmin={
              <SidebarButton
                href="/dashboard/patients?status=ACTIVE"
                isActive={patientIsActive}
              >
                <SidebarButtonImage
                  src={adminPatientIcon}
                  alt="Ícone de pacientes"
                />
                <SidebarButtonText>Gestão de Clientes</SidebarButtonText>
              </SidebarButton>
            }
            renderIfUser={null}
          />

          <AccessControl
            role={session.data?.user.role}
            renderIfAdmin={null}
            renderIfUser={
              <SidebarButton
                href={`/dashboard/clinics/${session.data?.user.clinics?.[0]?.id}/patients`}
                isActive={clinicIsActive}
              >
                <SidebarButtonImage
                  src={userPatientIcon}
                  alt="Ícone de pacientes"
                />
                <SidebarButtonText>Clientes</SidebarButtonText>
              </SidebarButton>
            }
          />

          <SidebarButton
            href="/dashboard/simulation"
            isActive={isSimulationActive}
          >
            {simulationIcon}
            <SidebarButtonText>Solicitações</SidebarButtonText>
          </SidebarButton>
        </SidebarItems>

        <Button
          className="flex h-16 w-full justify-start gap-2 rounded-[4px] bg-brand-grayscale-1 bg-transparent px-6 py-3 font-semibold text-brand-grayscale-7 hover:bg-transparent"
          onClick={handleSignOutClick}
        >
          <GoSignOut size={24} />
          Sair
        </Button>
      </SidebarContent>
    </_Sidebar>
  )
}

export default Sidebar
